import * as React from 'react';
import moment from 'moment';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { ReactSortable } from 'react-sortablejs';
import { ConsentRequestTemplate, ConsentRequestTemplateField } from '../../../../../../models/consentRequestTemplate';
import { Button } from '../../../../../../components/Button/Button';
import { BurgerIcon } from 'Src/components/SvgIcons/BurgerIcon';
import { INPUT_MASK_VALIDATION } from '../../../../../../consts/date';
import './SchoolEventConsentRequestForm.scss';
import { FormDefinition, FormElement } from 'Src/types/formSchema';
import { formDefinitionToLegacyFields } from 'Src/helpers/form/legacyFormAdapter';
import { legacyFieldsToFormDefinition, normalizeLegacyQuestionTitle } from 'Src/helpers/form/legacyFormAdapter';
import { SendTimeFormEditor } from 'Src/components/SendTimeFormEditor/SendTimeFormEditor';
import { stripUnsignedFormMediaKeys, uploadFormFile, uploadFormImage } from 'Src/helpers/form/formMedia';
import { AppUser } from 'Src/views/App/App';
import { validateFormDefinitionStructure } from 'Src/helpers/form/formDefinitionValidation';

const Sortable = ReactSortable as any;

interface Props {
  user?: AppUser;
  template?: ConsentRequestTemplate;
  onCancelClick: () => void;
  onSendConsentRequestsClick: (
    fields: ConsentRequestFormField[],
    deadlineForAnswers: string,
    formDefinition?: FormDefinition
  ) => void;
  defaultBuilderMode?: 'COMPACT' | 'FULL';
}

interface State {
  fields: ConsentRequestFormField[];
  elements: Array<FormElement & { isChecked: boolean }>;
  formDefinition: FormDefinition;
  deadlineForAnswers: Date | null;
  deadlineForAnswersRaw: string;
  isDeadlineForAnswersError: boolean;
}

export interface ConsentRequestFormField extends ConsentRequestTemplateField {
  isChecked: boolean;
  id: number;
}

export class ConsentRequestForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const template = props.template;
    const propsFields = [...(template?.fields || [])];
    const isV2 = template?.schemaVersion === 2 && !!template.formDefinition;

    this.state = {
      deadlineForAnswers: null,
      deadlineForAnswersRaw: '',
      isDeadlineForAnswersError: false,
      fields: propsFields.map((field, index) => ({
        ...field,
        isChecked: field.isDefault,
        id: index
      })),
      elements: isV2
        ? template.formDefinition.elements.map(element => ({
            ...element,
            isChecked: true
          }))
        : [],
      formDefinition: isV2
        ? {
            ...template.formDefinition,
            elements: template.formDefinition.elements.map(element => ({
              ...element,
              title: normalizeLegacyQuestionTitle(element.title)
            }))
          }
        : legacyFieldsToFormDefinition(propsFields)
    };
  }

  onCheckboxClick = index => {
    const { fields } = this.state;
    const nextFields = fields.map((field, fieldIndex) => {
      if (fieldIndex === index) {
        return {
          ...field,
          isChecked: !field.isChecked
        };
      } else {
        return field;
      }
    });

    this.setState({
      fields: nextFields
    });
  };

  sortFields = sortedFields => {
    this.setState({
      fields: sortedFields
    });
  };

  getDependencyClosure = (rootIds: Set<string>): Set<string> => {
    const { elements } = this.state;
    const included = new Set(rootIds);
    let changed = true;

    while (changed) {
      changed = false;
      elements.forEach(element => {
        if (!included.has(element.id)) return;
        (element.visibility?.conditions || []).forEach(condition => {
          if (!included.has(condition.fieldId)) {
            included.add(condition.fieldId);
            changed = true;
          }
        });
      });

      (this.props.template?.formDefinition?.logic || []).forEach(rule => {
        rule.when.conditions.forEach(condition => {
          if (!included.has(condition.fieldId)) {
            included.add(condition.fieldId);
            changed = true;
          }
        });
      });
    }

    return included;
  };

  getEffectiveSelectedElementIds = (): Set<string> => {
    const roots = new Set(
      this.state.elements.filter(element => element.kind !== 'QUESTION' || element.isChecked).map(element => element.id)
    );
    return this.getDependencyClosure(roots);
  };

  isRequiredByConditionalLogic = (elementId: string): boolean => {
    const rootsWithoutElement = new Set(
      this.state.elements
        .filter(element => element.id !== elementId && (element.kind !== 'QUESTION' || element.isChecked))
        .map(element => element.id)
    );
    return this.getDependencyClosure(rootsWithoutElement).has(elementId);
  };

  renderFields(): React.ReactNode {
    const { fields, elements } = this.state;
    const isV2 = elements.length > 0;

    if (isV2) {
      return (
        <Sortable
          list={elements}
          setList={(next: Array<FormElement & { isChecked: boolean }>) => this.setState({ elements: next })}
          handle=".eConsentRequestFieldsDrag"
          animation={150}
        >
          {elements.map((element, index) => {
            const isQuestion = element.kind === 'QUESTION';
            const isRequiredByLogic = isQuestion && this.isRequiredByConditionalLogic(element.id);
            const isEffectivelyChecked = element.isChecked || isRequiredByLogic;
            return (
              <div className="bConsentRequestFields" key={element.id}>
                <div className="pl-3 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    {isQuestion ? (
                      <div className="custom-control custom-switch">
                        <input
                          id={`switch_${element.id}`}
                          onChange={() =>
                            !isRequiredByLogic &&
                            this.setState({
                              elements: elements.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, isChecked: !item.isChecked } : item
                              )
                            })
                          }
                          checked={isEffectivelyChecked}
                          disabled={isRequiredByLogic}
                          type="checkbox"
                          className="custom-control-input"
                        />
                        <label className="custom-control-label pl-3 mb-3 mt-3" htmlFor={`switch_${element.id}`}>
                          {element.title}
                          {isRequiredByLogic && <span className="text-muted ml-2">(required by logic)</span>}
                        </label>
                      </div>
                    ) : (
                      <div className="my-3">
                        <span className="badge badge-light border mr-2">Content</span>
                        {element.title}
                      </div>
                    )}
                    <span className="badge badge-info ml-2">{element.type}</span>
                  </div>

                  <div className="eConsentRequestFieldsDrag">
                    <BurgerIcon />
                  </div>
                </div>
              </div>
            );
          })}
        </Sortable>
      );
    }

    return (
      <Sortable list={fields} setList={this.sortFields} handle=".eConsentRequestFieldsDrag" animation={150}>
        {fields.map((field, index) => (
          <div className="bConsentRequestFields" key={`consent_request_field_${field._id || field.id}`}>
            <div className="pl-3 d-flex align-items-center justify-content-between">
              <div className={'custom-control custom-switch'}>
                <input
                  id={`switch_${field._id || field.id}`}
                  onChange={() => this.onCheckboxClick(index)}
                  checked={field.isChecked}
                  type="checkbox"
                  className="custom-control-input"
                />
                <label className="custom-control-label pl-3 mb-3 mt-3" htmlFor={`switch_${field._id || field.id}`}>
                  {field.heading}
                </label>
              </div>

              <div className="eConsentRequestFieldsDrag">
                <BurgerIcon />
              </div>
            </div>
          </div>
        ))}
      </Sortable>
    );
  }

  onSelectAllClick = () => {
    const { fields, elements } = this.state;
    if (elements.length > 0) {
      const questions = elements.filter(element => element.kind === 'QUESTION');
      const shouldSelect = !questions.every(element => element.isChecked);
      this.setState({
        elements: elements.map(element =>
          element.kind === 'QUESTION' ? { ...element, isChecked: shouldSelect } : element
        )
      });
      return;
    }
    const isSelectAllChecked = fields.every(field => field.isChecked);

    this.setState({
      fields: fields.map(field => {
        return {
          ...field,
          isChecked: !isSelectAllChecked
        };
      })
    });
  };

  renderSelectAll(): React.ReactNode {
    const { fields, elements } = this.state;
    const isSelectAllChecked =
      elements.length > 0
        ? elements.filter(element => element.kind === 'QUESTION').every(element => element.isChecked)
        : fields.every(field => field.isChecked);

    return (
      <div className="bConsentRequestFields">
        <div className="pl-3">
          <div className={'custom-control custom-switch'} key={`consent_request_field_selectAll`}>
            <input
              id={`switch_selectAll`}
              onChange={this.onSelectAllClick}
              checked={isSelectAllChecked}
              type="checkbox"
              className="custom-control-input"
            />
            <label className="custom-control-label pl-3 mb-3 mt-3" htmlFor={`switch_selectAll`}>
              {isSelectAllChecked ? 'Unselect All' : 'Select All'}
            </label>
          </div>
        </div>
      </div>
    );
  }

  onSendClick = () => {
    const { formDefinition, deadlineForAnswersRaw } = this.state;
    const sanitizedDefinition = stripUnsignedFormMediaKeys(formDefinition);
    const { onSendConsentRequestsClick } = this.props;
    if (validateFormDefinitionStructure(sanitizedDefinition).length > 0) return;
    const checkedFields = (formDefinitionToLegacyFields(sanitizedDefinition).map((field, index) => ({
      ...field,
      isChecked: true,
      id: index
    })) as unknown) as ConsentRequestFormField[];

    if (!deadlineForAnswersRaw) {
      onSendConsentRequestsClick(checkedFields, '', sanitizedDefinition);
      return;
    }

    const m = moment(deadlineForAnswersRaw, INPUT_MASK_VALIDATION, true);
    const isOk = m.isValid() && m.isAfter(moment());

    if (!isOk) {
      this.setState({ isDeadlineForAnswersError: true });
      return;
    }

    onSendConsentRequestsClick(checkedFields, deadlineForAnswersRaw, sanitizedDefinition);
  };

  renderButtons(): React.ReactNode {
    const { onCancelClick } = this.props;
    return (
      <>
        <Button onClick={onCancelClick} text={'Cancel'} customClass={'btn-secondary mt-3 mr-3'} />
        <Button onClick={this.onSendClick} text={'Send consent request'} customClass={'mt-3'} />
      </>
    );
  }

  isValidFutureDeadlineString = (value: string): boolean => {
    if (!value) return true;
    const m = moment(value, INPUT_MASK_VALIDATION, true);
    return m.isValid() && m.isAfter(moment());
  };

  setDeadlineFromDate = (date: Date | null) => {
    this.setState({
      deadlineForAnswers: date,
      deadlineForAnswersRaw: date ? moment(date).format(INPUT_MASK_VALIDATION) : '',
      isDeadlineForAnswersError: false
    });
  };

  onDeadlineChange = (date: Date | null) => {
    this.setDeadlineFromDate(date);
  };

  onDeadlineChangeRaw = (event: any) => {
    const raw = event?.target?.value || '';

    this.setState({ deadlineForAnswersRaw: raw });

    if (!raw) {
      this.setState({ deadlineForAnswers: null, isDeadlineForAnswersError: false });
      return;
    }

    const m = moment(raw, INPUT_MASK_VALIDATION, true);

    if (!m.isValid()) {
      this.setState({ isDeadlineForAnswersError: true });
      return;
    }

    this.setState({
      deadlineForAnswers: m.toDate(),
      isDeadlineForAnswersError: !m.isAfter(moment())
    });
  };

  validateDeadlineNow = () => {
    const { deadlineForAnswersRaw } = this.state;

    const ok = this.isValidFutureDeadlineString(deadlineForAnswersRaw);

    this.setState({ isDeadlineForAnswersError: !ok });
    return ok;
  };

  renderDeadline(): React.ReactNode {
    const { deadlineForAnswers } = this.state;

    return (
      <SitDatePicker
        mode="datetime"
        value={deadlineForAnswers}
        onChange={this.onDeadlineChange}
        placeholder="Select date and time"
        format="DD/MM/YYYY HH:mm"
        min={new Date()}
        minuteStep={5}
        clearable
        placement="top"
        align="right"
        className="consentSendFlow__dateTimePicker"
      />
    );
  }

  render() {
    const { formDefinition, isDeadlineForAnswersError } = this.state;
    const questionCount = formDefinition.elements.filter(element => element.kind === 'QUESTION').length;
    const structureIssues = validateFormDefinitionStructure(formDefinition);

    return (
      <div className="consentSendFlow">
        <SendTimeFormEditor
          definition={formDefinition}
          onChange={next => this.setState({ formDefinition: next })}
          uploadImage={this.props.user ? file => uploadFormImage(this.props.user!, file) : undefined}
          uploadFile={this.props.user ? file => uploadFormFile(this.props.user!, file) : undefined}
          user={this.props.user}
          templateType={this.props.user ? 'CONSENT' : undefined}
          initialTemplateId={this.props.template?.id}
          defaultTemplateName={this.props.template?.name || 'Consent request form'}
          onCancelRequested={this.props.onCancelClick}
          onSendRequested={this.onSendClick}
          defaultBuilderMode="COMPACT"
          showTemplateSaveAction={true}
          sendButtonText="Request consent"
          footerBeforeActions={
            <>
              <div className="consentSendFlow__deadlineRow">
                <div>
                  <strong>Deadline for answers</strong>
                  <span>Optional. Leave empty when responses have no deadline.</span>
                </div>
                {this.renderDeadline()}
              </div>
              {structureIssues.length > 0 && (
                <div className="consentSendFlow__error">
                  Fix the highlighted form configuration issues before sending.
                </div>
              )}
              {isDeadlineForAnswersError && (
                <div className="consentSendFlow__error">Please select a future date and time.</div>
              )}
              <div className="consentSendFlow__questionSummary">{questionCount} questions will be answered</div>
            </>
          }
        />
      </div>
    );
  }
}
